import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { LocalClientDto } from '../../dtos/local-client.dto';
import { StaffRosterDto } from '../../dtos/staff-roster.dto';

export interface CreateLocalClientPayload {
  readonly Username: string;
  // Omitted (never an empty string) creates the account through the invitation flow.
  readonly Password?: string;
  readonly FirstName: string;
  readonly LastName: string;
  readonly RoleId: number | null;
  readonly DepartmentId: number | null;
  readonly LinkedProviderUserExternalId?: string | null;
}

export interface UpdateLocalClientPayload {
  readonly Username: string;
  readonly Password: string | null;
  readonly FirstName: string;
  readonly LastName: string;
  readonly RoleId: number;
  readonly DepartmentId: number | null;
  readonly IsActive: boolean;
}

@Injectable()
export class LocalUsersRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(organizationId: number): Observable<readonly LocalClientDto[]> {
    return this.http.get<readonly LocalClientDto[]>(`${this.apiUrl}${API_ENDPOINTS.organizationClients.list(organizationId)}`);
  }

  public getRoster(organizationId: number): Observable<StaffRosterDto> {
    return this.http.get<StaffRosterDto>(`${this.apiUrl}${API_ENDPOINTS.organizationClients.roster(organizationId)}`);
  }

  public create(organizationId: number, payload: CreateLocalClientPayload): Observable<LocalClientDto> {
    return this.http.post<LocalClientDto>(`${this.apiUrl}${API_ENDPOINTS.organizationClients.list(organizationId)}`, { ...payload, AccountRealm: 'administration' });
  }

  public update(organizationId: number, clientId: number, payload: UpdateLocalClientPayload): Observable<LocalClientDto> {
    return this.http.put<LocalClientDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.byId(organizationId, clientId)}`, payload,
    );
  }

  public deactivate(organizationId: number, clientId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.organizationClients.byId(organizationId, clientId)}`);
  }

  public deletePermanently(organizationId: number, clientId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.permanentDelete(organizationId, clientId)}`,
    );
  }

  public resendInvitation(organizationId: number, clientId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.resendInvitation(organizationId, clientId)}`,
      null,
    );
  }
}
